<script setup lang="ts">
import { CloseOutlined } from "@ant-design/icons-vue";
import type { ButtonProps, DrawerProps } from "ant-design-vue";
import { computed, useSlots } from "vue";

type PickDrawerProps = Pick<
	DrawerProps,
	| "autofocus"
	| "bodyStyle"
	| "contentWrapperStyle"
	| "destroyOnClose"
	| "forceRender"
	| "getContainer"
	| "keyboard"
	| "mask"
	| "maskClosable"
	| "maskStyle"
	| "placement"
	| "push"
	| "size"
	| "zIndex"
	| "height"
	| "width"
	| "onAfterOpenChange"
	| "onClose"
	| "title"
	| "rootClassName"
	| "rootStyle"
>;

interface CustomDrawerProps extends PickDrawerProps {
	okText?: string;
	okType?: ButtonProps["type"];
	okButtonProps?: ButtonProps;
	confirmLoading?: boolean;
	cancelText?: string;
	showFooter?: boolean;
	cancelButtonProps?: ButtonProps;
	showCloseIcon?: boolean;
}

const props = withDefaults(defineProps<CustomDrawerProps>(), {
	autofocus: true,
	keyboard: true,
	mask: true,
	maskClosable: true,
	placement: "right",
	push: () => ({ distance: 180 }),
	size: "default",
	zIndex: 1000,
	height: 378,
	width: 378,
	bodyStyle: () => ({ padding: "20px 24px" }),
	okText: "确定",
	okType: "primary",
	cancelText: "取消",
	getContainer: "body",
	showCloseIcon: true,
	showFooter: true,
});

const emits = defineEmits<{
	ok: [];
	cancel: [];
}>();

const drawerProps = computed(() => {
	const { title, ...rest } = props;

	const dp = Object.fromEntries(
		Object.entries(rest).filter(([_, v]) => v !== undefined)
	) as unknown as Omit<CustomDrawerProps, "title">;

	return {
		...dp,
	};
});

const hasTitleSlot = computed(() => !!useSlots().title);
const hasFooterSlot = computed(() => !!useSlots().footer);

const open = defineModel("open", {
	type: Boolean,
	required: true,
	default: false,
});

const handleCancel = () => {
	emits("cancel");
	open.value = false;
};

const handleConfirm = () => {
	emits("ok");
};
</script>

<template>
	<a-drawer v-model:open="open" :closable="false" v-bind="drawerProps" :class="['custom-drawer']">
		<template #title>
			<div class="px-ml py-mm min-h-ml relative font-medium">
				<template v-if="hasTitleSlot">
					<slot name="title"></slot>
				</template>

				<template v-else>
					{{ props.title }}
				</template>

				<div
					v-if="showCloseIcon"
					class="right-ml top-half absolute -translate-y-1/2 cursor-pointer text-black"
					@click="open = false"
				>
					<CloseOutlined />
				</div>
			</div>
		</template>

		<slot></slot>

		<template v-if="showFooter && hasFooterSlot" #footer>
			<slot name="footer"></slot>
		</template>

		<template v-else-if="showFooter" #footer>
			<div class="gap-ms flex items-center justify-end">
				<a-button class="w-5xh" v-bind="cancelButtonProps" @click="handleCancel">
					{{ cancelText }}
				</a-button>
				<a-button
					class="w-5xh"
					v-bind="okButtonProps"
					:type="okType"
					:loading="confirmLoading"
					@click="handleConfirm"
				>
					{{ okText }}
				</a-button>
			</div>
		</template>
	</a-drawer>
</template>

<style lang="scss">
.custom-drawer {
	.ant-drawer-body {
		padding: 0;
	}

	.ant-drawer-header {
		padding: 0;
		border-bottom: 1px solid var(--color-wireframe);
	}

	.ant-drawer-footer {
		padding: 20px 24px;
		border-top: 1px solid var(--color-wireframe);
	}
}
</style>
